<script setup>
import Layout from "../Layouts/Customer.vue";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import {useForm} from "@inertiajs/inertia-vue3";
import {ref, computed, onMounted} from "vue";
import {useToast} from "primevue/usetoast";
import Calendar from "primevue/calendar";
import {required$} from "../utils/validators";
import {useVuelidate} from "@vuelidate/core";
import {scrollToFirstError} from "../utils/form";
import Tag from "primevue/tag";
import Card from "primevue/card";
import moment from 'moment'
import DeviceReasonsOP from "../Components/DeviceReasonsOP.vue";
import {Link} from "@inertiajs/inertia-vue3";
import SimplybookIcon from "../Components/SimplybookIcon.vue";

const props = defineProps([
    'customer',
    'customerUnlimitedPlan',
    'menuItems',
    'flash',
    'unlimitedPlanOptions',
    'statusDataMap',
    'maybeDevicesMap',
    'deviceReasonsMap',
    'unlimitedPlanMonthsMap',
    'fitnessDevices',
    'totalFitnessBookings'
]);

const toast = useToast();
const submitting = ref(false)
const deleting = ref(false)
const isVisibleDialog = ref(false)
const isVisibleDeleteDialog = ref(false)
const customerUnlimitedPlan = ref(props.customerUnlimitedPlan)
const hasCustomerUnlimitedPlan = ref(Boolean(customerUnlimitedPlan.value))
const deviceReasonsOP = ref(null)
// Renewal feature refs
const isRenewal = ref(false)
const renewalConfirmDialog = ref(false)

const form = useForm({
    unlimited_plan_id: hasCustomerUnlimitedPlan.value ? customerUnlimitedPlan.value.unlimited_plan_id : null,
    date: hasCustomerUnlimitedPlan.value ? new Date(customerUnlimitedPlan.value.date) : null,
    date_to: hasCustomerUnlimitedPlan.value ? new Date(customerUnlimitedPlan.value.date_to) : null,
    customer_id: props.customer.id,
});

const rules = {
    form: {
        unlimited_plan_id: {
            required$,
        },

        date: {
            required$,
        },

        date_to: {
            required$,
            custom: {
                $validator: function(value) {
                    if (! value || ! form.date) {
                        return true;
                    }

                    return (moment(value).isSameOrBefore(moment(form.date)) === false)
                },
                $message: 'Das Bis-Datum muss nach dem Von-Datum liegen.',
                $params: {
                    type: 'custom'
                }
            }
        }
    }
}

const v$ = useVuelidate(rules, {
    form,
})

const dateFrom = computed(() => {
    if (! hasCustomerUnlimitedPlan.value) {
        return null
    }

    return moment(customerUnlimitedPlan.value.date).format('DD.MM.YYYY')
})

const dateTo = computed(() => {
    if (! hasCustomerUnlimitedPlan.value) {
        return null
    }

    return moment(customerUnlimitedPlan.value.date_to).format('DD.MM.YYYY')
})

const hasMaybeDevice = computed(() => {
    if (! hasCustomerUnlimitedPlan.value) {
        return false
    }

    let value = false

    customerUnlimitedPlan.value.unlimited_plan.devices.some((v) => {
        if (typeof props.maybeDevicesMap[v.name] != 'undefined') {
            value = true

            return true;
        }

        return false;
    })

    return value
})

const maybeDevices = computed(() => {
    if (! hasCustomerUnlimitedPlan.value) {
        return false
    }

    const devices = []

    customerUnlimitedPlan.value.unlimited_plan.devices.every((v) => {
        if (typeof props.maybeDevicesMap[v.name] != 'undefined') {
            v.pivot.warning_type = 'maybe'

            devices.push(v)
        }

        return true;
    })

    return devices
})


const handleDateChange = (newDate) => {
    if (newDate) {
        form.date =  new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000);

        if (! form.date_to) {
            form.date_to = moment(form.date).add( props.unlimitedPlanMonthsMap[form.unlimited_plan_id], 'months')
                .subtract(1, 'days')
                .toDate()
        }
    }
}

const handleDateToChange = (newDate) => {
    if (newDate) {
        form.date_to =  new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000);

        v$.value.form.date_to.$touch()
    }
}

const submit = () => {
    v$.value.$validate()

    if (v$.value.$errors.length) {
        scrollToFirstError()

        return
    }

    submitting.value = true

    const postUrl = hasCustomerUnlimitedPlan.value
        ? `/customer-unlimited-plans/${customerUnlimitedPlan.value.uuid}`
        : '/customer-unlimited-plans'

    form.post(postUrl, {
        preserveState: false,

        onSuccess: (page) => {
            toast.add({
                severity: "success",
                summary: "Aktualisiert",
                life: 5000,
            });
        },

        onFinish: (visit) => {
            submitting.value = false;
            isVisibleDialog.value = false
        }
    })
}

const deleteUnlimitedPlan = () => {
    deleting.value = true

    useForm({}).delete(`/customer-unlimited-plans/${customerUnlimitedPlan.value.uuid}`, {
        preserveState: false,

        onSuccess: (page) => {
            toast.add({
                severity: "success",
                summary: "Aktualisiert",
                life: 5000,
            });
        },

        onFinish: (visit) => {
            deleting.value = false;
            isVisibleDeleteDialog.value = false
        }
    })
}

const togglePopOver = (e, device) => {
    deviceReasonsOP.value.togglePopOver(e, device)
}

const onChangeUnlimitedPlanId = () => {
    v$.value.form.unlimited_plan_id.$touch()

    form.date = null
    form.date_to = null

    v$.value.form.date.$touch()
    v$.value.form.date_to.$touch()
}

// Renewal feature functions
const openRenewalDialog = () => {
    isRenewal.value = true
    
    // Pre-fill form with current plan data
    form.unlimited_plan_id = customerUnlimitedPlan.value.unlimited_plan_id
    form.date = new Date() // Today
    
    // Calculate new date_to based on plan duration
    const months = props.unlimitedPlanMonthsMap[form.unlimited_plan_id] || 1
    const newDateTo = new Date()
    newDateTo.setMonth(newDateTo.getMonth() + months)
    newDateTo.setDate(newDateTo.getDate() - 1) // Subtract 1 day
    form.date_to = newDateTo
    
    isVisibleDialog.value = true
}

const openAddDialog = () => {
    isRenewal.value = false
    
    // Reset form for new plan
    form.unlimited_plan_id = null
    form.date = null
    form.date_to = null
    
    isVisibleDialog.value = true
}

const handleSubmit = () => {
    v$.value.$validate()

    if (v$.value.$errors.length) {
        scrollToFirstError()
        return
    }

    if (isRenewal.value) {
        // Show confirmation dialog for renewal
        renewalConfirmDialog.value = true
        return
    }
    
    // Regular submit for new plan
    submit()
}

const confirmRenewal = () => {
    // Submit to the actual renewal endpoint using form helper
    form.post(`/customers/${props.customer.uuid}/unlimited-plan/renew`, {
        preserveScroll: true,
        preserveState: false,
        onSuccess: () => {
            // Show success toast
            toast.add({
                severity: "success",
                summary: "Plan erfolgreich verlängert",
                detail: "Der aktuelle Plan wurde geschlossen und ein neuer Plan wurde erstellt.",
                life: 5000,
            })
            
            // Close dialogs
            renewalConfirmDialog.value = false
            isVisibleDialog.value = false
            isRenewal.value = false
            
            // Reset form
            form.reset()
        },
        onError: (errors) => {
            console.error('Renewal failed:', errors)
            
            // Show error toast
            if (errors.plan) {
                toast.add({
                    severity: "error",
                    summary: "Fehler",
                    detail: errors.plan[0] || "Plan konnte nicht verlängert werden",
                    life: 5000,
                })
            } else {
                toast.add({
                    severity: "error",
                    summary: "Fehler",
                    detail: "Plan konnte nicht verlängert werden",
                    life: 5000,
                })
            }
            
            renewalConfirmDialog.value = false
        }
    })
}

onMounted(() => {
    if (props.customer?.is_measurement_stale) {
        toast.add({
            severity: 'error',
            summary: 'Messung veraltet',
            detail: 'Die Messungen dieses Kunden wurden seit über 3 Wochen nicht aktualisiert.',
            life: 0,
            closable: true
        });
    }
});

</script>
<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <div class="py-7 text-sm">
            <div class="px-14">
                <div class="flex justify-end mb-7 gap-2">
                    <Button
                        v-if="! hasCustomerUnlimitedPlan"
                        label="Fitness hinzufügen"
                        severity="success"
                        icon="pi pi-plus"
                        class="text-sm"
                        @click="openAddDialog" />
                    
                    <Button
                        v-if="hasCustomerUnlimitedPlan"
                        label="Verlängern"
                        icon="pi pi-refresh"
                        class="text-sm"
                        style="background-color: #6B7280; border-color: #6B7280"
                        @click="openRenewalDialog" />
                    
                    <Link v-if="hasCustomerUnlimitedPlan" 
                          :href="`/customers/${customer.uuid}/fitness/history`"
                          class="text-sm">
                        <Button
                            label="Historie"
                            severity="secondary"
                            icon="pi pi-history"
                            class="text-sm"
                            size="small" />
                    </Link>
                </div>


                <div class="flex justify-center">
                    <Card v-if="hasCustomerUnlimitedPlan">
                        <template #title>{{ customerUnlimitedPlan.unlimited_plan.name }}</template>

                        <template #content>
                            <div class="mb-2">
                                {{ dateFrom }} - {{ dateTo }}
                            </div>

                            <div v-if="hasMaybeDevice"
                                 class="mb-2">
                                <div class="flex flex-col gap-y-1">
                                    <div v-for="data in maybeDevices"
                                         class="flex gap-3"
                                         @mouseenter="togglePopOver($event, data)">
                                        <i class="pi pi-exclamation-circle text-[#FFC000]"></i>

                                        <span class="text-xs font-extralight">{{ data.name }} - Freigabe durch VIGORY nötig</span>
                                    </div>
                                </div>
                            </div>

                            <div class="mb-2">
                                <Tag :severity="statusDataMap[customerUnlimitedPlan.status].severity"
                                     :value="statusDataMap[customerUnlimitedPlan.status].label"></Tag>
                            </div>
                        </template>

                        <template #footer>
                            <div class="flex justify-between">
                                <i
                                    class="pi pi-file-edit text-[#C5C6CC] cursor-pointer"
                                    @click="isVisibleDialog = true"></i>

                                <i
                                    class="pi pi-trash text-[#C5C6CC] cursor-pointer"
                                    @click="isVisibleDeleteDialog = true"></i>
                            </div>
                        </template>
                    </Card>

                    <div v-else>
                        Sie haben keinen Fitnessplan
                    </div>
                </div>

                <!-- Fitness Devices Table -->
                <div v-if="fitnessDevices && fitnessDevices.length > 0" class="mt-9">
                    <div class="bg-[#EEEBE4] rounded-t-lg">
                        <div class="px-14 py-4">
                            <h3 class="text-lg font-bold">Geräte</h3>
                        </div>
                    </div>
                    
                    <div class="bg-white rounded-b-lg shadow-sm">
                        <div class="px-14 py-4">
                            <!-- Table Header -->
                            <div class="grid grid-cols-6 gap-0 items-center mb-2">
                                <div class="col-span-4 text-sm font-bold">Gerät</div>
                                <div class="col-span-2 text-sm font-bold text-center">Buchungen</div>
                            </div>
                            
                            <!-- Device Rows -->
                            <div v-for="device in fitnessDevices" :key="device.id" 
                                 class="grid grid-cols-6 gap-0 items-center">
                                <div class="col-span-4 border-b border-[#C5C6CC] py-3">
                                    <span class="text-sm">{{ device.name }}</span>
                                </div>
                                <div class="col-span-2 border-b border-[#C5C6CC] py-3 text-center flex items-center justify-center gap-3">
                                    <Link :href="`/bookings/${customer.uuid}/${device.uuid}?source=fitness`"
                                          class="text-black hover:text-blue-600 underline text-sm font-bold">
                                        {{ device.bookingsCount }}
                                    </Link>
                                    <!-- SimplyBook Bookings Link -->
                                    <Link 
                                        :href="`/customers/${customer.uuid}/simplybook-bookings/${device.uuid}`"
                                        class="text-blue-600 hover:text-blue-700"
                                        title="SimplyBook Buchungen anzeigen"
                                    >
                                        <SimplybookIcon :size="14" />
                                    </Link>
                                </div>
                            </div>
                            
                            <!-- Total -->
                            <div class="mt-4">
                                <h4 class="text-sm font-bold">Total Einheiten: {{ totalFitnessBookings || 0 }}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Dialog v-model:visible="isVisibleDialog"
                :header="isRenewal ? 'Fitness Abo verlängern' : 'Fitness Abo'"
                :style="{ width: '25rem' }"
                modal>
            <div class="mb-7">
                <div class="mb-5">
                    <Dropdown
                        :invalid="v$.form.unlimited_plan_id.$error"
                        v-model="form.unlimited_plan_id"
                        :options="unlimitedPlanOptions"
                        option-label="label"
                        option-value="value"
                        class="text-sm w-full"
                        @change="onChangeUnlimitedPlanId" />

                    <div v-if="v$.form.unlimited_plan_id.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.unlimited_plan_id.$errors[0].$message }}
                    </div>
                </div>

                <div class="mb-2">
                    <Calendar
                        :invalid="v$.form.date.$error"
                        v-model="form.date"
                        showIcon
                        iconDisplay="input"
                        class="w-full"
                        dateFormat="dd.mm.yy"
                        placeholder="tt.mm.jjjj"
                        @date-select="handleDateChange($event)"
                        @change="v$.form.date.$touch"
                        :disabled="! form.unlimited_plan_id"
                    />

                    <div v-if="v$.form.date.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.date.$errors[0].$message }}
                    </div>
                </div>

                <div class="mb-2">
                    To
                </div>

                <div>
                    <Calendar
                        :invalid="v$.form.date_to.$error"
                        v-model="form.date_to"
                        showIcon
                        iconDisplay="input"
                        class="w-full"
                        dateFormat="dd.mm.yy"
                        placeholder="tt.mm.jjjj"
                        @date-select="handleDateToChange($event)"
                        @change="v$.form.date_to.$touch"
                        :disabled="! form.unlimited_plan_id"
                    />

                    <div v-if="v$.form.date_to.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.date_to.$errors[0].$message }}
                    </div>
                </div>
            </div>


            <div class="flex justify-end gap-2">
                <Button
                    type="button"
                    label="Abbrechen"
                    severity="secondary"
                    class="text-sm"
                    @click="isVisibleDialog = false" />

                <Button
                    :loading="submitting"
                    type="button"
                    :label="isRenewal ? 'Verlängern' : 'Speichern'"
                    class="text-sm"
                    @click="handleSubmit"></Button>
            </div>
        </Dialog>

        <Dialog v-model:visible="isVisibleDeleteDialog"
                header="Konfirmation"
                :style="{ width: '40rem' }"
                modal>
            <div class="mb-7">
                <div class="flex items-center gap-4">
                    <i class="pi pi-exclamation-triangle text-2xl"></i>

                    Sind Sie sicher, dass Sie {{ customerUnlimitedPlan.unlimited_plan.name }} löschen wollen?
                </div>
            </div>


            <div class="flex justify-end gap-2">
                <Button
                    type="button"
                    label="Abbrechen"
                    severity="secondary"
                    class="text-sm"
                    @click="isVisibleDeleteDialog = false" />

                <Button
                    :loading="deleting"
                    type="button"
                    label="Löschen"
                    class="text-sm"
                    @click="deleteUnlimitedPlan"></Button>
            </div>
        </Dialog>

        <!-- Renewal Confirmation Dialog -->
        <Dialog v-model:visible="renewalConfirmDialog" 
                header="Verlängerung bestätigen" 
                :style="{ width: '30rem' }"
                modal>
            <div class="mb-6">
                <div class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <div class="flex items-center gap-2 text-yellow-800">
                        <i class="pi pi-exclamation-triangle text-xl"></i>
                        <p class="font-semibold">
                            Der aktuelle Plan wird geschlossen und kann nicht mehr bearbeitet werden.
                        </p>
                    </div>
                </div>
                
                <div class="mb-4 p-3 bg-gray-50 rounded">
                    <h4 class="font-semibold mb-2 text-gray-700">Aktueller Plan:</h4>
                    <div class="text-sm text-gray-600">
                        <p class="mb-1">
                            <span class="font-medium">Plan:</span> 
                            {{ customerUnlimitedPlan?.unlimited_plan?.name || 'N/A' }}
                        </p>
                        <p>
                            <span class="font-medium">Gültig bis:</span> 
                            {{ dateTo || 'N/A' }}
                        </p>
                    </div>
                </div>
                
                <div class="p-3 bg-green-50 rounded">
                    <h4 class="font-semibold mb-2 text-green-700">Neuer Plan:</h4>
                    <div class="text-sm text-green-600">
                        <p class="mb-1">
                            <span class="font-medium">Plan:</span> 
                            {{ unlimitedPlanOptions.find(p => p.value === form.unlimited_plan_id)?.label || 'N/A' }}
                        </p>
                        <p class="mb-1">
                            <span class="font-medium">Von:</span> 
                            {{ form.date ? moment(form.date).format('DD.MM.YYYY') : 'N/A' }}
                        </p>
                        <p>
                            <span class="font-medium">Bis:</span> 
                            {{ form.date_to ? moment(form.date_to).format('DD.MM.YYYY') : 'N/A' }}
                        </p>
                    </div>
                </div>
            </div>
            
            <div class="flex justify-end gap-2">
                <Button 
                    label="Abbrechen" 
                    @click="renewalConfirmDialog = false" 
                    severity="secondary"
                    class="text-sm" />
                <Button 
                    label="Verlängerung bestätigen" 
                    @click="confirmRenewal"
                    severity="success"
                    class="text-sm" />
            </div>
        </Dialog>

        <DeviceReasonsOP ref="deviceReasonsOP"
                         :device-reasons-map="deviceReasonsMap"></DeviceReasonsOP>
    </Layout>
</template>
